<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.groups', 2),
                        href: meta.links.index,
                    },
                    {
                        name: group.name,
                        href: meta.links.group_show,
                    },
                ]"
            >
                <template v-slot:corner>
                    <ActivityMenuOptions>
                        <GroupSelector :grade-levels="meta.links.gradeLevels" :user-grade-levels="[]">
                            <template v-slot:default="{ filteredGradeLevels }">
                                <div class="mb-2" v-for="gradeLevel in filteredGradeLevels" :key="gradeLevel.id">
                                    <AppButton
                                        element="inertia-link"
                                        v-for="group in gradeLevel.groups"
                                        :href="group.url"
                                        :key="group.id"
                                        desired-action
                                        class="mr-2"
                                    >
                                        {{ group.name }}
                                    </AppButton>
                                </div>
                            </template>
                        </GroupSelector>
                    </ActivityMenuOptions>
                </template>
            </SmartBreadcrumbs>
        </template>

        <div class="flex justify-between items-center">
            <div class="flex space-x-2 items-center">
                <NavLinks :links="meta.navDaysLinks" />
            </div>
        </div>

        <form :action="meta.links.show" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-12">
                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.date')" for="date" />
                        <FormInput required id="date" v-model="form.date" name="date" type="date" class="w-full mt-1" />
                        <FormError :value="form.errors.date" class="mt-2" />
                    </div>
                </div>
                <div class="flex justify-end space-x-2 items-center">
                    <SubmitFormButton :button-text="$t('messages.filter')" :processing="form.processing" is-filter />
                </div>
            </div>
        </form>

        <div class="md:grid md:grid-cols-12 md:gap-6 mt-5">
            <div class="col-span-4">
                <div class="flex justify-between items-center">
                    <div v-for="i in [0, 1, 2, 3, 4]" :key="i" class="relative">
                        <label>
                            <input
                                type="checkbox"
                                name="mood_score"
                                :value="i"
                                class="sr-only"
                                v-model="form.mood_score"
                            />
                            <SmileyPollEmojiImage
                                :number="i"
                                class="h-16 w-16 cursor-pointer"
                                :class="{ 'opacity-50': !form.mood_score.includes(i) }"
                            />
                        </label>
                        <div class="absolute bottom-0 right-0 pointer-events-none">
                            <BadgePill color="indigo"> &times; {{ group.moodScoreMap[i] }} </BadgePill>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="mt-6">
            <div>
                <TransitionGroup
                    enter-active-class="transition-opacity ease-linear duration-300"
                    enter-from-class="opacity-0"
                    enter-to-class="opacity-100"
                    leave-active-class="transition-opacity ease-linear duration-300"
                    leave-from-class="opacity-100"
                    leave-to-class="opacity-0"
                    tag="div"
                    class="grid grid-cols-1 gap-y-2 gap-x-4 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4"
                >
                    <div
                        class="relative col-span-1 flex shadow rounded-md"
                        v-for="student in filteredStudents"
                        :key="student.id"
                        :class="{
                            'opacity-50': student.absent && settings.show === 'distinguish_absent_students',
                        }"
                    >
                        <div
                            class="shrink-0 flex items-center justify-center w-16 text-white text-sm leading-5 font-medium rounded-l-md overflow-hidden"
                        >
                            <inertia-link :href="student.links.show">
                                <img :src="student.avatar_or_default" alt="profile photo" />
                            </inertia-link>
                        </div>
                        <div
                            class="flex-1 flex flex-col justify-start border-t border-r border-b border-gray-200 bg-white rounded-r-md truncate relative"
                        >
                            <div class="flex flex-1 justify-between px-4 pt-1 text-sm leading-5 truncate">
                                <inertia-link
                                    :href="student.links.show"
                                    class="text-gray-900 font-medium hover:text-gray-600 transition ease-in-out duration-150"
                                >
                                    {{ student.index }}. {{ student.full_name }}
                                </inertia-link>
                            </div>
                            <div class="px-4">
                                <p class="text-gray-600 text-xs whitespace-normal">{{ student.moodCheck.comment }}</p>
                            </div>
                            <SmileyPollEmojiImage
                                :number="student.moodCheck.mood_score"
                                class="h-6 w-6 cursor-pointer absolute top-1 right-1"
                            />
                        </div>
                    </div>
                </TransitionGroup>
            </div>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import AppButton from '@/components/AppButton.vue';
import ActivityMenuOptions from '@/components/pageSpecific/ActivityMenuOptions.vue';
import GroupSelector from '@/components/GroupSelector.vue';
import SmileyPollEmojiImage from '@/components/SmileyPollEmojiImage.vue';
import BadgePill from '@/components/BadgePill.vue';
import NavLinks from '@/components/NavLinks.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        PanelPage,
        AppButton,
        ActivityMenuOptions,
        GroupSelector,
        SmileyPollEmojiImage,
        BadgePill,
        NavLinks,
        FormLabel,
        FormInput,
        FormError,
        SubmitFormButton,
    },
    layout: Layout,
    name: 'Show',
    props: ['teacher', 'meta', 'options', 'group', 'students', 'filters'],
    data() {
        return {
            form: this.$inertia.form({
                date: this.filters.date,
                mood_score: [0, 1, 2, 3, 4],
            }),
            showOptions: [
                {
                    value: 'all',
                    text: this.$t('messages.all'),
                },
                {
                    value: 'distinguish_absent_students',
                    text: this.$t('messages.distinguish_absent_students'),
                },
                {
                    value: 'hide_absent_students',
                    text: this.$t('messages.hide_absent_students'),
                },
            ],
            settings: {
                show: 'distinguish_absent_students',
            },
        };
    },
    methods: {
        toggleMode() {
            this.mode = this.mode === 'team' ? 'individual' : 'team';
        },
        filter() {
            this.form.get(this.meta.links.show);
        },
    },
    computed: {
        selectedStudents: function () {
            return this.students.filter((s) => s.selected);
        },
        filteredStudents: function () {
            return this.students.filter((s) => {
                return this.form.mood_score.includes(s.moodCheck.mood_score);
            });
        },
    },
};
</script>
